import {
  WorkspaceActions,
  WorkspaceActionTypes,
} from './workspace.actions';
import * as Model from './workspace.model';
import {
  cloneDeep
} from 'lodash';
import * as moment from 'moment';
import {
  SortDescriptor,
  orderBy
} from '@progress/kendo-data-query';
import {
  AppState
} from '..';
import {
  createSelector
} from '@ngrx/store';

export interface WorkspaceState {
  searchByOptions: Model.SearchByOptions;
  payPeriods: Model.TTMap<Model.PayPeriod>;
  categories: Model.TTMap<Model.Category>;
  clients: Model.TTMap<Model.ClientDTO>;
  timesheets: Model.TTMap<Model.TimeSheet>;
  descriptions: Model.TTMap<Model.TTMap<Model.Description>> ;
  staffs: Model.TTMap<Model.StaffDTO>;
  staff: Model.StaffDTO;
  staffCodes: Model.StaffCodeDTO[];
  selectedStaffID: number;
  notification: Model.Message;
  alert: Model.Message;
  numberOfClientsInSheet: number;
  totalHoursInSheet: number;
  totalHoursUnsubmitted: number;
  view: Model.View;
  summary: Model.TimeSummary;
  summaryRows: Model.TTMap<Model.TimeSummaryRow>;
  paidTimeOff: Model.PTO;
  loading: boolean;
  gridPageSize: number | 'all';
  summaryPageSize: number | 'all';
  categoriesPageSize: number | 'all';
  descriptionsPageSize: number | 'all';
  clientsPageSize: number | 'all';
  staffPageSize: number | 'all';
  detailSort: SortDescriptor[];
  summarySort: SortDescriptor[];
  staffList: Model.StaffDTO[];
}

export const InitialState: WorkspaceState = {
  searchByOptions: {
    dateOption: Model.SearchDateOptions.PayPeriod,
    client: null,
    staff: null,
    category: null,
    dateRange: {
      DateStarting: null,
      DateEnding: null
    }
  },
  categories: {},
  payPeriods: {},
  clients: {},
  staffs: {},
  timesheets: {},
  descriptions: {},
  staff: undefined,
  staffCodes: [],
  selectedStaffID: null,
  notification: null,
  alert: null,
  numberOfClientsInSheet: 0,
  totalHoursInSheet: 0,
  totalHoursUnsubmitted: 0,
  view: Model.View.detail,
  summary: null,
  summaryRows: {},
  paidTimeOff: undefined,
  staffList: [],
  loading: false,
  gridPageSize: 'all',
  summaryPageSize: 10,
  categoriesPageSize: 10,
  descriptionsPageSize: 10,
  clientsPageSize: 10,
  staffPageSize: 10,
  detailSort: [{
    field: 'dateWorked',
    dir: 'desc',
  }, {
    field: 'startTime',
    dir: 'desc'
  }, {
    field: 'endTime',
    dir: undefined
  }, {
    field: 'hoursBreak',
    dir: undefined,
  }, {
    field: 'clientName',
    dir: undefined,
  }, {
    field: 'billingDescription',
    dir: undefined,
  }],
  summarySort: [{
    field: 'clientName',
    dir: 'asc',
  }, {
    field: 'payPeriodEnding',
    dir: undefined
  }, {
    field: 'hoursBillable',
    dir: undefined
  }, {
    field: 'submittedDate',
    dir: undefined,
  }]
};

export function reducer(state = InitialState, action: WorkspaceActions): WorkspaceState {
  switch (action.type) {
    case WorkspaceActionTypes.SET_SEARCH_BY_DATE_OPTION:
      return setSearchByDateOption(state, action.payload);
    case WorkspaceActionTypes.LOAD_PAY_PERIODS:
      return state;
    case WorkspaceActionTypes.SET_PAY_PERIODS:
      return setPayPeriods(state, action.payload);
    case WorkspaceActionTypes.SELECT_PAY_PERIOD:
      return selectPayPeriod(state, action.payload);
    case WorkspaceActionTypes.SET_CLIENTS:
      return {
        ...state,
        clients: getClientMap(action.payload)
      };
    case WorkspaceActionTypes.SET_STAFFS:
      return {
        ...state,
        staffs: getStaffMap(action.payload)
      };
    case WorkspaceActionTypes.SET_STAFF_CODES:
      return setStaffCodes(state, action.payload);
    case WorkspaceActionTypes.SELECT_CLIENT:
      return {
        ...state,
        searchByOptions: {
          ...state.searchByOptions,
          client: action.payload,
        }
      };
    case WorkspaceActionTypes.SELECT_STAFF:
      return {
        ...state,
        searchByOptions: {
            ...state.searchByOptions,
            staff: action.payload
          },
          staff: action.payload
      };
    case WorkspaceActionTypes.SET_SELECTED_STAFF_ID:
      return {
        ...state,
        selectedStaffID: action.payload
      };
    case WorkspaceActionTypes.SET_TIMESHEETS:
      return setTimesheets(state, action.payload);
    case WorkspaceActionTypes.SET_DESCRIPTIONS:
      return setDescriptions(state, action.payload);
    case WorkspaceActionTypes.SEND_NOTIFICATION:
      return {
        ...state,
        notification: action.payload
      };
    case WorkspaceActionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notification: null
      };
    case WorkspaceActionTypes.DELETE_TIMESHEET:
      return deleteTimesheet(state, action.payload);
    case WorkspaceActionTypes.LOAD_SUMS:
      return loadSums(state);
    case WorkspaceActionTypes.SET_VIEW:
      return {
        ...state,
        view: action.payload
      };
    case WorkspaceActionTypes.SET_UNSUBMITTED_SUMMARY_ROWS:
      return setUnsubmittedSummaryRows(state, action.payload);
    case WorkspaceActionTypes.SET_SUMMARY_ROWS:
      return setSummaryRows(state, action.payload);
    case WorkspaceActionTypes.SET_PTO:
        return setPaidTimeOff(state, action.payload);
    case WorkspaceActionTypes.UPDATE_SUMMARY_ROW:
      return updateSummaryRow(state, action.payload);
    case WorkspaceActionTypes.SELECT_DATE_RANGE:
      return selectDateRange(state, action.payload);
    case WorkspaceActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    case WorkspaceActionTypes.SET_PAGE_SIZE:
      return {
        ...state,
        gridPageSize: action.payload
      };
      case WorkspaceActionTypes.SET_SUMMARIES_PAGE_SIZE:
        return {
          ...state,
          summaryPageSize: action.payload
        };
    case WorkspaceActionTypes.SET_CATEGORIES_PAGE_SIZE:
      return {
        ...state,
        categoriesPageSize: action.payload
      };
    case WorkspaceActionTypes.SET_CLIENTS_PAGE_SIZE:
      return {
        ...state,
        clientsPageSize: action.payload
      };
    case WorkspaceActionTypes.SET_DESCRIPTIONS_PAGE_SIZE:
      return {
        ...state,
        descriptionsPageSize: action.payload
      };
    case WorkspaceActionTypes.SET_SORT:
      if (action.payload.view === Model.View.detail) {
        return {
          ...state,
          detailSort: action.payload.sort
        };
      }
      return {
        ...state,
        summarySort: action.payload.sort
      };
    case WorkspaceActionTypes.ADD_ALERT:
      return {
        ...state,
        alert: action.payload
      };
    case WorkspaceActionTypes.REMOVE_ALERT:
      return removeAlert(state, action.payload);
    case WorkspaceActionTypes.SET_STAFF_LIST:
      return {
        ...state,
        staffList: action.payload
      };
    case WorkspaceActionTypes.SELECT_STAFF_SEARCH:
      return {
        ...state,
        searchByOptions: {
          ...state.searchByOptions,
          staff: action.payload
        }
      };
    case WorkspaceActionTypes.SET_CATEGORIES:
      return {
        ...state,
        categories: getMap(action.payload, 'categoryID')
      };
    case WorkspaceActionTypes.SELECT_CATEGORY_FOR_SEARCH:
      return {
        ...state,
        searchByOptions: {
          ...state.searchByOptions,
          category: action.payload
        }
      }
    case WorkspaceActionTypes.SET_STAFF_PAGE_SIZE:
      return {
        ...state,
        staffPageSize: action.payload
      };
      default:
        return state;
  }
}

function removeAlert(state: WorkspaceState, id: string) {
  const s = cloneDeep(state);
  if (s.alert && s.alert.id === id) {
    s.alert = null;
  }
  return s;
}

function selectDateRange(state: WorkspaceState, payload: Model.DateRangeOutput) {
  const s = cloneDeep(state);
  if (payload.type === 'all') {
    s.searchByOptions.dateRange = {
      DateStarting: null,
      DateEnding: null
    };
    return s;
  }
  if (payload.type === 'start') {
    s.searchByOptions.dateRange.DateStarting = payload.value;
    return s;
  }
  s.searchByOptions.dateRange.DateEnding = payload.value;
  return s;
}

function updateSummaryRow(state: WorkspaceState, payload: {
  before: Model.TimeSummaryRow;after: Model.TimeSummaryRow
}) {
  const s = cloneDeep(state);
  delete s.summaryRows[payload.before.id];
  s.summaryRows[payload.after.id] = payload.after;
  return s;
}

function setUnsubmittedSummaryRows(state: WorkspaceState, timeSummaryRows: Model.TimeSummaryRow[]) {
  const s = cloneDeep(state);
  let rs = cloneDeep(timeSummaryRows);
  rs = parseSummaryRowDates(rs);
  const map = getMap(rs, 'id');
  s.summaryRows = map;
  return s;
}

function setSummaryRows(state: WorkspaceState, timeSummaryRows: Model.TimeSummaryRow[]) {
  const s = cloneDeep(state);
  let rs = cloneDeep(timeSummaryRows);
  rs = rs.map( ts => {
    if (state.clients[ts.clientID]) {
      ts.clientName = state.clients[ts.clientID].clientName;
    }
    return ts;
  });
  rs = parseSummaryRowDates(rs);
  const map = {};
  for (let i = 0; i<rs.length; i++) {
    map[i] = rs[i];
  }
  s.summaryRows = map;
  return s;
}

function parseSummaryRowDates(rows: Model.TimeSummaryRow[]) {
  return rows.map(row => {
    row.payPeriodEnding = moment(row.payPeriodEnding).toDate();
    row.payPeriodStarting = moment(row.payPeriodStarting).toDate();
    row.submittedDate = moment(row.submittedDate).toDate();
    return row;
  });
}

function getMap<T > (arr: T[], idField: string): Model.TTMap<T > {
  const map = {};
  for (const entity of arr) {
    map[entity[idField]] = entity;
  }
  return map;
}

function loadSums(state: WorkspaceState) {
  const s = cloneDeep(state);
  const seenClients = {};
  const sheets = Object.values(state.timesheets);
  s.numberOfClientsInSheet = 0;
  s.totalHoursInSheet = 0;
  s.totalHoursUnsubmitted = 0;

  sheets.forEach(sheet => {
    s.totalHoursInSheet += sheet.hoursBillable;

    if (!sheet.timeSummaryIdx) {
      s.totalHoursUnsubmitted += sheet.hoursBillable;
    }

    if (!seenClients[sheet.clientID]) {
      s.numberOfClientsInSheet += 1;
      seenClients[sheet.clientID] = true;
    }
  });
  return s;
}

function setPaidTimeOff(state: WorkspaceState, payload: Model.PTO) {
  const s = cloneDeep(state);
  s.paidTimeOff = payload;
  return s;
}

function setStaffCodes(state: WorkspaceState, payload: Model.StaffCodeDTO[]) {
  const s = cloneDeep(state);
  s.staffCodes = payload;
  return s;
}

function deleteTimesheet(state: WorkspaceState, timesheet: Model.TimeSheet) {
  const s = cloneDeep(state);
  delete s.timesheets[timesheet.timeSheetIdx];
  return s;
}

function setDescriptions(state: WorkspaceState, descriptions: Model.Description[]) {
  const s = cloneDeep(state);
  descriptions.forEach(description => {
    if (!s.descriptions[description.clientID]) {
      s.descriptions[description.clientID] = {};
    }
    s.descriptions[description.clientID][description.descriptionID] = description;
  });
  return s;
}

function setTimesheets(state: WorkspaceState, sheets: Model.TimeSheet[]) {
  const s = cloneDeep(state);
  const map = {};
  sheets.forEach(ts => {
    const sheet = cloneDeep(ts);
    sheet.dateWorked = moment(ts.dateWorked).toDate();
    sheet.startTime = moment(ts.startTime).toDate();
    sheet.endTime = moment(ts.endTime).toDate();
    sheet.staffName = state.staffList.find(staff => staff.staffID === sheet.staffID).staffName;
    map[ts.timeSheetIdx] = sheet;
  });
  s.timesheets = map;
  return s;
}

function getClientMap(clients: Model.ClientDTO[]): Model.TTMap<Model.ClientDTO > {
  const map = {};
  clients.forEach(client => map[client.clientID] = client);
  return map;
}

function getStaffMap(staffs: Model.StaffDTO[]): Model.TTMap<Model.StaffDTO > {
  const map = {};
  staffs.forEach(staff => map[staff.staffID] = staff);
  return map;
}

function selectPayPeriod(state: WorkspaceState, payPeriod ? : Model.PayPeriod) {
  const s = cloneDeep(state);
  if (!payPeriod) {
    s.searchByOptions.payPeriod = getCurrentPayPeriod(s.payPeriods);
    return s;
  }
  s.searchByOptions.payPeriod = payPeriod;
  return s;
}

export function getCurrentPayPeriod(map: Model.TTMap<Model.PayPeriod > ): Model.PayPeriod {
  const today = new Date().setHours(0, 0, 0, 0);
  for (const p of Object.values(map)) {
    const start = moment(p.payPeriodStarting).toDate().setHours(0, 0, 0, 0);
    const end = moment(p.payPeriodEnding).toDate().setHours(0, 0, 0, 0);
    if (today >= start && today <= end) {
      return p;
    }
  }
}

function parsePayPeriodDTO(dto: Model.PayPeriodDTO): Model.PayPeriod {
  return {
    payPeriodEndingIdx: dto.payPeriodEndingIdx,
    payPeriodStarting: new Date(Date.parse(dto.payPeriodStarting)),
    payPeriodEnding: new Date(Date.parse(dto.payPeriodEnding)),
    text: ''
  };
}

function setPayPeriods(state: WorkspaceState, payPeriods: Model.PayPeriodDTO[]): WorkspaceState {
  const s = cloneDeep(state);
  const map = {};
  payPeriods.map((dto: Model.PayPeriodDTO) => {
    const payPeriod = parsePayPeriodDTO(dto);
    const opts = {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    } as Intl.DateTimeFormatOptions;
    // tslint:disable-next-line: max-line-length
    payPeriod.text = `${payPeriod.payPeriodStarting.toLocaleString('en-US', opts)} - ${payPeriod.payPeriodEnding.toLocaleString('en-US', opts)}`;
    map[payPeriod.payPeriodEndingIdx] = payPeriod;
  });
  s.payPeriods = map;
  return s;
}

function setSearchByDateOption(state: WorkspaceState, option: Model.SearchDateOptions) {
  const s = cloneDeep(state);
  s.searchByOptions.dateOption = option;
  return s;
}

export function getPayPeriodsArray(state: WorkspaceState) {
  return Object.values(state.payPeriods);
}

export function getClientsArray(state: WorkspaceState) {
  return Object.values(state.clients);
}

export const selectWorkspaceState = (state: AppState) => state.workspace;

export const selectTimesheetsForGrid = createSelector(selectWorkspaceState, (state: WorkspaceState) => {
  const keys = Object.keys(state.timesheets);
  const sheets = [];
  for (const key of keys) {
    const sheet = cloneDeep(state.timesheets[key]);
    sheet.clientName = state.clients[sheet.clientID].clientName;
    sheets.push(sheet);
  }
  return orderBy(sheets, state.detailSort);
});

export const selectSummaryRowsForGrid = createSelector(selectWorkspaceState, (state: WorkspaceState) =>
  orderBy(Object.values(state.summaryRows), state.summarySort)
);
